import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { debounce } from "@mui/material";
import { CustomDialog, HorizontalDivider, IconComponent, Selector, icon } from "../../../../common/components";
import styles from "./AssociateContentDialog.module.css";
import { isSegFeatureEnabled, SegFlags, translate } from "../../../../common/providers";
import { isNonEmptyArray } from "../../../../common/utils";
import { useApi } from "../../../../common/hooks";
import { ProjectDocumentService, SearchCardService } from "../../../../api";
import DocumentAutoCompleteSearch from "../../../../common/components/autocomplete-text-field/document-autocomplete-search/DocumentAutocompleteSearch";
import ContentAutoCompleteSearch from "../../../my-review/components/right-panels/options-panel/tabs/link-tab/components/content-autocomplete-search/ContentAutocompleteSearch";
import { OPERATORS } from "../../../my-review/constants/constants";
import ContentToLink from "../../../my-review/components/right-panels/options-panel/tabs/link-tab/components/content-to-link/ContentToLink";

const page = 0;
const limit = 20;

const AssociateContentDialog = ({ open, onClose, onSubmit, projectId }) => {
	const CONTENT_TYPES = [
		{ value: "COMMITMENT", label: translate("commitment-tracking.associate-content.type-commitment") },
		{ value: "PENALTY", label: translate("commitment-tracking.associate-content.type-penalty") },
	];
	const [contentType, setContentType] = useState("COMMITMENT");
	const sort = useSelector(({ mySearch }) => mySearch.selectedSortAlgo);
	const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
	const [isLoadingContents, setIsLoadingContents] = useState(false);
	const [isSelectedResult, setIsSelectedResult] = useState(false);
	const [searchDocument, setSearchDocument] = useState([]);
	const [initialDocuments, setInitialDocuments] = useState([]);
	const [initialContents, setInitialContents] = useState([]);
	const [searchContents, setSearchContents] = useState({});
	const [searchDocumentInputValue, setSearchDocumentInputValue] = useState("");
	const [searchContentInputValue, setSearchContentInputValue] = useState("");
	const [selectedDocument, setSelectedDocument] = useState(null);
	const [selectedContent, setSelectedContent] = useState(null);
	const [searchDebouncedDocument, setSearchDebouncedDocument] = useState("");
	const [searchDebouncedContent, setSearchDebouncedContent] = useState("");

	const { call: searchDocs } = useApi(ProjectDocumentService.search);
	const { call: searchContent } = useApi(SearchCardService.search);

	useEffect(() => {
		if (!open) {
			setSearchDocumentInputValue("");
			setSearchContentInputValue("");
			setContentType("COMMITMENT");
			setSelectedContent(null);
			setSelectedDocument(null);
			setSearchDocument([]);
			setSearchContents({});
			setIsSelectedResult(false);
		}
	}, [open]);

	const handleSelectType = (e) => {
		const { value } = e.target;
		setContentType(value);
	};

	useEffect(() => {
		if (projectId && open) {
			setIsLoadingDocuments(true);
			setIsLoadingContents(true);
			searchDocs({ projectId }, { name: "" })
				.then((data) => {
					setInitialDocuments(data);
					setSearchDocument(data);
				})
				.catch(console.error)
				.finally(() => setIsLoadingDocuments(false));
			const contentPayload = {
				separator: OPERATORS.AND,
				Type: "InformationSearchFilterDTO",
				isFilterTocOut: false,
			};
			if (selectedDocument) {
				contentPayload.documents = { is: true, matchAll: true, elements: [selectedDocument] };
			}
			searchContent({ projectId }, contentPayload, 0, 10, null)
				.then((data) => {
					setSearchContents(data);
					setInitialContents(data);
				})
				.catch(console.error)
				.finally(() => setIsLoadingContents(false));
		}
	}, [projectId, searchDocs, searchContent, open, selectedDocument]);

	useEffect(() => {
		if (
			searchDebouncedDocument &&
			searchDebouncedDocument === searchDocumentInputValue &&
			searchDebouncedDocument.length > 1
		) {
			searchDocs({ projectId }, { name: searchDebouncedDocument })
				.then((data) => setSearchDocument(data))
				.catch(console.error)
				.finally(() => setIsLoadingDocuments(false));
		}
	}, [projectId, searchDocs, searchDebouncedDocument, searchDocumentInputValue]);

	const debouncedSearch = debounce((name) => {
		setSearchDebouncedDocument(name);
	}, 500);

	const handleSearchDocument = (name) => {
		setSearchDocumentInputValue(name);
		if (!name) {
			setSearchDocument(initialDocuments);
			setIsLoadingDocuments(false);
			return;
		}
		setIsLoadingDocuments(true);
		debouncedSearch(name);
	};

	const handleDocumentSelection = ({ documentId }) => {
		setSelectedDocument(documentId);
	};

	const handleResetDocument = () => {
		setSelectedDocument(null);
		setSearchDocument(initialDocuments);
	};

	useEffect(() => {
		if (searchDebouncedContent === searchContentInputValue) {
			if (searchDebouncedContent && searchDebouncedContent.length > 0) {
				const filters = {
					separator: OPERATORS.AND,
					Type: "InformationSearchFilterDTO",
					isFilterTocOut: false,
					keywordFilters: {
						filters: [{ keywords: [searchDebouncedContent], operator: "IS_EXACTLY", matchAll: true }],
					},
				};
				const regex = /^(req_|info_)(\d+)$/i;
				const match = searchDebouncedContent.match(regex);
				if (match) {
					const [, , numbers] = match;
					filters.informationIds = { elements: [Number.parseInt(numbers, 10)] };
					delete filters.keywordFilters;
				}
				if (selectedDocument) {
					filters.documents = { is: true, matchAll: true, elements: [selectedDocument] };
				}
				searchContent({ projectId }, filters, page, limit, sort.trim()?.length === 0 ? null : sort)
					.then((data) => setSearchContents(data))
					.catch(console.error)
					.finally(() => setIsLoadingContents(false));
			} else {
				setSearchContents(initialContents);
				setIsLoadingContents(false);
			}
		}
	}, [
		projectId,
		searchContent,
		initialContents,
		searchDebouncedContent,
		searchContentInputValue,
		selectedDocument,
		sort,
	]);

	const debouncedSearchContents = debounce((name) => {
		setSearchDebouncedContent(name);
	}, 500);

	const handleSearchContent = (name) => {
		if (!name) {
			setSearchContents(initialContents);
		}
		setSearchContentInputValue(name);
		setIsLoadingContents(true);
		debouncedSearchContents(name);
	};

	const handleContentSelection = (content) => {
		setSelectedContent(content);
		setIsSelectedResult(true);
	};

	const handleResetContent = () => {
		setSearchContents(initialContents);
		setSelectedContent(null);
	};

	const handleSubmit = () => {
		const { informationId } = selectedContent;
		onSubmit({ informationId, type: contentType });
	};

	const generateSearchResultLabel = () => {
		if (searchDebouncedContent === searchContentInputValue) {
			if (searchDebouncedContent.length >= 1 && searchContents?.resultCount === 0) {
				return translate("commitment-tracking.associate-content.no-content");
			}
		}
		return "";
	};

	return (
		<CustomDialog
			disableBackdropClick
			isForm
			open={open}
			submitDisabled={(isSegFeatureEnabled(SegFlags.DISABLED) ? false : !contentType) || !selectedContent}
			title={translate("commitment-tracking.associate-content.title")}
			onClose={onClose}
			onSubmit={handleSubmit}
		>
			<div className={styles.block}>
				<div>
					<span className={styles.title}>{translate("commitment-tracking.associate-content.document")}</span>
					<span>{translate("commitment-tracking.associate-content.document-optional")}</span>
				</div>
				<div>
					<DocumentAutoCompleteSearch
						documents={searchDocument?.contents || []}
						emptyStateLabel={translate("common:document-autocomplete-search.empty-state")}
						error={false}
						inputValue={searchDocumentInputValue}
						isLoading={isLoadingDocuments}
						label={translate("commitment-tracking.associate-content.document-search")}
						onReset={handleResetDocument}
						onSearch={handleSearchDocument}
						onSelect={handleDocumentSelection}
					/>
				</div>
			</div>
			<HorizontalDivider className={styles.divider} />
			<div className={styles.block}>
				<div>
					<span className={styles.title}>
						{translate("commitment-tracking.associate-content.content-label")}
					</span>
					<IconComponent
						className={styles.infoIcon}
						color="var(--red-500)"
						icon={icon.faAsterisk}
						size="2xs"
					/>
				</div>
				{(!selectedContent && (
					<div>
						<ContentAutoCompleteSearch
							contents={searchContents?.details?.contents || []}
							emptyStateLabel={generateSearchResultLabel()}
							error={false}
							inputValue={searchContentInputValue}
							isLoading={isLoadingContents}
							label={translate("commitment-tracking.associate-content.content-search")}
							onReset={handleResetContent}
							onSearch={handleSearchContent}
							onSelect={handleContentSelection}
						/>
					</div>
				)) || (
					<ContentToLink
						content={selectedContent}
						isSelectedResult={isSelectedResult}
						onClose={handleResetContent}
					/>
				)}
			</div>
			{isSegFeatureEnabled(SegFlags.DISABLED) && (
				<div className={styles.block}>
					<div>
						<span className={styles.title}>
							{translate("commitment-tracking.associate-content.type-label")}
						</span>
						<IconComponent
							className={styles.infoIcon}
							color="var(--red-500)"
							icon={icon.faAsterisk}
							size="2xs"
						/>
					</div>
					<Selector
						fullWidth
						autoTranslate="no"
						className={styles.formControl}
						disabled={!isNonEmptyArray(CONTENT_TYPES)}
						itemRenderer={(item) => CONTENT_TYPES.find((type) => type.value === item)?.label}
						items={CONTENT_TYPES.map((ct) => ct.value)}
						labelClassName={styles.selector_label}
						value={contentType}
						valueRenderer={(value) => CONTENT_TYPES.find((ct) => ct.value === value)?.label || value}
						onChange={handleSelectType}
					/>
				</div>
			)}
		</CustomDialog>
	);
};

export default AssociateContentDialog;
